import { useState, useEffect, useMemo, useRef } from "react";
import { Lock } from "lucide-react";
import HubSpotInlineForm from "@/components/HubSpotInlineForm";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";

const LS_KEY = "newsletter_subscribed";
const GATE_MIN_WORDS = 1000;

const BOT_UA = /googlebot|bingbot|yandex|baiduspider|duckduckbot|slurp|msnbot|teoma|crawl|spider|mediapartners/i;

const NEWSLETTER_FORM_ID = "e49b7f08-4495-4cb4-afa2-b485f007f8a2";

interface ContentGateProps {
  wordCount: number;
  content: string;
  renderContent: (md: string, showDropCap: boolean) => React.ReactNode;
}

/**
 * Splits markdown content at roughly 40% of H2 sections (minimum 2 visible).
 * Returns [aboveGate, belowGate] markdown strings.
 */
function splitContent(content: string): [string, string] {
  const h2Regex = /^## (?!\[|!\[)/gm;
  const matches: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = h2Regex.exec(content)) !== null) {
    matches.push(m.index);
  }

  if (matches.length <= 2) {
    const mid = Math.floor(content.length * 0.5);
    const breakAt = content.indexOf("\n\n", mid);
    if (breakAt === -1) return [content, ""];
    return [content.slice(0, breakAt), content.slice(breakAt)];
  }

  // Keep at least the final two H2 sections (e.g. Conclusion + closing CTA) above the gate.
  const maxSplitIdx = matches.length - 2;
  const desiredIdx = Math.max(2, Math.ceil(matches.length * 0.7));
  const splitIdx = Math.min(desiredIdx, maxSplitIdx);
  if (splitIdx < 2) return [content, ""];
  const splitPos = matches[splitIdx];
  return [content.slice(0, splitPos), content.slice(splitPos)];
}

const ContentGate = ({ wordCount, content, renderContent }: ContentGateProps) => {
  const lang = useLanguage();
  const t = uiT(lang);
  const [unlocked, setUnlocked] = useState(false);
  const [skipped, setSkipped] = useState(false);

  const isBot = typeof navigator !== "undefined" && BOT_UA.test(navigator.userAgent);
  const shouldGate = wordCount >= GATE_MIN_WORDS && !isBot;

  useEffect(() => {
    if (typeof window !== "undefined" && localStorage.getItem(LS_KEY)) {
      setUnlocked(true);
    }
  }, []);

  const [above, below] = useMemo(() => {
    if (!shouldGate || unlocked || skipped) return [content, ""];
    const [a, b] = splitContent(content);
    if (b.length / content.length < 0.3) return [content, ""];
    return [a, b];
  }, [content, shouldGate, unlocked, skipped]);

  const savedScrollY = useRef<number | null>(null);

  const handleSkip = () => {
    savedScrollY.current = window.scrollY;
    setSkipped(true);
  };

  const handleUnlock = () => {
    savedScrollY.current = window.scrollY;
    setUnlocked(true);
  };

  const handleFormSubmitted = () => {
    localStorage.setItem(LS_KEY, "1");
    handleUnlock();
  };

  useEffect(() => {
    if ((skipped || unlocked) && savedScrollY.current !== null) {
      requestAnimationFrame(() => {
        const scrollY = savedScrollY.current;
        if (scrollY !== null) window.scrollTo(0, scrollY);
        savedScrollY.current = null;
      });
    }
  }, [skipped, unlocked]);

  if (!shouldGate || unlocked || skipped || !below) {
    return <>{renderContent(content, true)}</>;
  }

  return (
    <div>
      {renderContent(above, true)}

      <div className="relative mt-0">
        <div className="h-32 bg-gradient-to-t from-background to-transparent -mt-32 relative z-10 pointer-events-none" />

        <div className="relative z-10 rounded-2xl bg-gradient-to-br from-primary/[0.14] via-primary/[0.08] to-accent/[0.10] border border-primary/15 p-8 sm:p-10 overflow-hidden my-8">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative flex items-start gap-5 mb-5">
            <div className="hidden sm:flex items-center justify-center w-14 h-14 text-accent shrink-0">
              <Lock size={32} strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground leading-snug">
                {t("continueReading")}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                {t("gateSubtitle")}
              </p>
            </div>
          </div>

          <div className="relative qbd-newsletter-form qbd-newsletter-form--light qbd-newsletter-form--compact text-left">
            <HubSpotInlineForm formId={NEWSLETTER_FORM_ID} onFormSubmitted={handleFormSubmitted} />
          </div>

          <div className="relative mt-3 flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground/50">
              {t("noSpam")}
            </p>
            <button
              type="button"
              onClick={handleSkip}
              className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
            >
              {t("skipForNow")}
            </button>
          </div>
        </div>

        <div
          aria-hidden="false"
          className="max-h-0 overflow-hidden opacity-0"
        >
          {renderContent(below, false)}
        </div>
      </div>
    </div>
  );
};

export default ContentGate;